import {
  Box,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Typography,
} from '@mui/material'
import {useCallback, useMemo, useState} from 'react'
import {Link, useLocation, matchPath} from 'react-router-dom'
import PeopleIcon from '@mui/icons-material/People'
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts'
import FactoryIcon from '@mui/icons-material/Factory'
import CategoryIcon from '@mui/icons-material/Category'
import AppsIcon from '@mui/icons-material/Apps'
import {useAuth} from '../hooks/useAuth'
import {SiteSettingModel} from '../../../models/SiteSettingModel'
import {OpenRegistrationModal} from './OpenRegistrationModal'
import { getSiteSettings } from '../AuthApi'
// import {SUPER_ADMIN_EMAIL_REGEX} from '../../../config/env'

export const AuthProfileIcon = () => {
  const {user, logout} = useAuth()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const location = useLocation()
  const [modalOpen, setModalOpen] = useState(false)
  const [siteSettings, setSiteSettings] = useState<SiteSettingModel | null>(null)

  const handleOpen = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleCloseModal = () => {
    setModalOpen(false)
  }

  const handleUpdateSiteSettings = (newSettings: SiteSettingModel) => {
    setSiteSettings(newSettings)
  }

  const handleOpenModal = useCallback(async() => {
    const {data} = await getSiteSettings()
    setSiteSettings(data)

    setModalOpen(true)
    handleClose()
  }, [handleClose])

  const links = useMemo(() => {
    const links = [
      {to: '/users', label: 'Users', icon: <PeopleIcon />},
      {to: '/roles', label: 'Roles', icon: <ManageAccountsIcon />},
    ]
    if (user?.isAdmin) {
      links.push(
        {to: '/sectors', label: 'Sectors', icon: <FactoryIcon />},
        {to: '/categories', label: 'Categories', icon: <AppsIcon />},
        {to: '/entities', label: 'Entities', icon: <CategoryIcon />}
      )
    }
    return links
  }, [user])

  const initials = useMemo(() => {
    if (user) {
      const words = user.name.split(' ')
      const firstName = words[0]
      const lastName = words.length > 1 ? words[words.length - 1] : ''
      const firstLetter = firstName.charAt(0)
      const lastLetter = lastName.charAt(0)
      return `${firstLetter}${lastLetter}`
    }
  }, [user])

  const isOpen = !!anchorEl

  if (!user) {
    return null
  }

  return (
    <>
      <Button
        sx={{
          width: '2.8rem',
          height: '2.407rem',
          padding: 0,
          minWidth: 'unset',
          fontWeight: 'bold',
          fontSize: '1.5rem',
        }}
        variant='contained'
        onClick={handleOpen}
        id='auth-profile-icon'
      >
        {initials}
      </Button>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box>
          <Box sx={{p: 2}}>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <Typography sx={{fontWeight: 'bold', mr: 1}}>{user.name}</Typography>
              {user.role && <Chip size='small' label={user.role.name} color='primary' />}
            </Box>
            <Typography component='p' variant='subtitle2'>
              {user.email}
            </Typography>
          </Box>
          {user.isAdmin && (
            <>
              <Divider />
              <List>
                {links.map((link) => (
                  <ListItem
                    key={link.to}
                    disablePadding
                    selected={
                      !!matchPath(
                        {
                          path: link.to,
                          end: false,
                        },
                        location.pathname
                      )
                    }
                  >
                    <ListItemButton
                      id={`auth-admin-nav-list-${link.label.toLowerCase()}`}
                      component={Link}
                      to={link.to}
                      onClick={handleClose}
                    >
                      <ListItemIcon>{link.icon}</ListItemIcon>
                      <ListItemText primary={link.label} />
                    </ListItemButton>
                  </ListItem>
                ))}
                <ListItem disablePadding>
                  <ListItemButton id={`auth-admin-nav-list-open`} onClick={handleOpenModal}>
                    <ListItemText primary='Open Registration' />
                  </ListItemButton>
                </ListItem>
              </List>
            </>
          )}
          <Divider />
          <Box sx={{p: 1, display: 'flex', justifyContent: 'end'}}>
            <Button
              id={`auth-button-logout`}
              variant='contained'
              color='primary'
              size='small'
              onClick={logout}
            >
              Logout
            </Button>
          </Box>
        </Box>
      </Popover>
      <OpenRegistrationModal
        open={modalOpen}
        onClose={handleCloseModal}
        onUpdate={handleUpdateSiteSettings}
        data={siteSettings}
      />
    </>
  )
}
